import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, Button, Badge, Spinner, Modal, Label } from 'flowbite-react';
import {
  HiBell,
  HiRefresh,
  HiCheckCircle,
} from 'react-icons/hi';
import AdminLayout from '@/components/layout/AdminLayout';

interface PanicAlert {
  id: string;
  type: 'panic' | 'fire' | 'medical' | 'security';
  tenant_id: string;
  tenant_uuid: string;
  activated_by: {
    user_id: string;
    name: string;
    email: string;
  };
  closed_by?: {
    user_id: string;
    name: string;
    email: string;
  } | null;
  status: 'active' | 'closed';
  activated_at: string;
  closed_at: string | null;
  emergency_contacts_notified: string[];
  created_at: string;
}

export default function AdminAlerts() {
  const { userProfile } = useAuth();
  const [alerts, setAlerts] = useState<PanicAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState<PanicAlert | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('panic_alerts')
        .select('*')
        .order('activated_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      setAlerts(data || []);
    } catch (error: any) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'superadmin') {
      loadAlerts();
    }
  }, [userProfile]);

  const closeAlert = async (alert: PanicAlert) => {
    try {
      const { error } = await supabase
        .from('panic_alerts')
        .update({
          status: 'closed',
          closed_at: new Date().toISOString(),
          closed_by: {
            user_id: userProfile?.id || '',
            name: userProfile?.name || '',
            email: userProfile?.email || '',
          },
        })
        .eq('id', alert.id);

      if (error) throw error;
      await loadAlerts();
    } catch (error: any) {
      console.error('Error closing alert:', error);
    }
  };

  const getTypeBadge = (type: string) => {
    const config = {
      panic: { color: 'failure' as const, label: 'Pánico' },
      fire: { color: 'failure' as const, label: 'Incendio' },
      medical: { color: 'warning' as const, label: 'Médico' },
      security: { color: 'info' as const, label: 'Seguridad' },
    };
    const typeConfig = config[type as keyof typeof config] || config.panic;
    return <Badge color={typeConfig.color}>{typeConfig.label}</Badge>;
  };

  const openDetailsModal = (alert: PanicAlert) => {
    setSelectedAlert(alert);
    setShowDetailsModal(true);
  };

  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <AdminLayout>
        <Card>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Acceso Denegado</h3>
            <p className="text-gray-600 dark:text-gray-400">No tienes permisos para acceder a esta página</p>
          </div>
        </Card>
      </AdminLayout>
    );
  }

  const activeAlerts = alerts.filter((a) => a.status === 'active');
  const closedAlerts = alerts.filter((a) => a.status === 'closed');

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Alertas de Pánico</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Gestiona las alertas de emergencia del sistema</p>
          </div>
          <Button color="gray" onClick={loadAlerts} className="gap-2 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
            <HiRefresh className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="bg-white dark:bg-gray-800">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Alertas Activas</p>
                  <p className="text-2xl font-bold text-red-600">{activeAlerts.length}</p>
                </div>
                <HiBell className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Alertas Cerradas</p>
                  <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{closedAlerts.length}</p>
                </div>
                <HiCheckCircle className="w-8 h-8 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" className="text-blue-600" />
          </div>
        ) : alerts.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800">
            <div className="py-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-lg">No se encontraron alertas</p>
            </div>
          </Card>
        ) : (
          <Card className="bg-white dark:bg-gray-800">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">Tipo</th>
                    <th scope="col" className="px-6 py-3">Tenant UUID</th>
                    <th scope="col" className="px-6 py-3">Activado Por</th>
                    <th scope="col" className="px-6 py-3">Fecha Activación</th>
                    <th scope="col" className="px-6 py-3">Estado</th>
                    <th scope="col" className="px-6 py-3">Contactos Notificados</th>
                    <th scope="col" className="px-6 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr key={alert.id} className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
                      <td className="px-6 py-4">{getTypeBadge(alert.type)}</td>
                      <td className="px-6 py-4 font-mono text-xs">{alert.tenant_uuid}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{alert.activated_by.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{alert.activated_by.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {new Date(alert.activated_at).toLocaleString('es-ES')}
                      </td>
                      <td className="px-6 py-4">
                        <Badge color={alert.status === 'active' ? 'failure' : 'success'}>
                          {alert.status === 'active' ? 'Activa' : 'Cerrada'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {alert.emergency_contacts_notified?.length || 0} contactos
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openDetailsModal(alert)}
                            size="sm"
                            color="gray"
                            className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                          >
                            Ver Detalles
                          </Button>
                          {alert.status === 'active' && (
                            <Button
                              onClick={() => closeAlert(alert)}
                              size="sm"
                              color="success"
                              className="gap-1"
                            >
                              <HiCheckCircle className="w-3 h-3" />
                              Cerrar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Modal de Detalles */}
        <Modal show={showDetailsModal} onClose={() => setShowDetailsModal(false)} size="lg">
          <div className="p-6 bg-white dark:bg-gray-800">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Detalles de la Alerta</h3>
            {selectedAlert && (
              <div className="space-y-4">
                <div>
                  <Label>Tipo</Label>
                  <div className="mt-1">{getTypeBadge(selectedAlert.type)}</div>
                </div>
                <div>
                  <Label>Tenant UUID</Label>
                  <p className="mt-1 font-mono text-sm text-gray-900 dark:text-white">{selectedAlert.tenant_uuid}</p>
                </div>
                <div>
                  <Label>Activado Por</Label>
                  <p className="mt-1 text-gray-900 dark:text-white">{selectedAlert.activated_by.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedAlert.activated_by.email}</p>
                </div>
                <div>
                  <Label>Fecha de Activación</Label>
                  <p className="mt-1 text-gray-900 dark:text-white">
                    {new Date(selectedAlert.activated_at).toLocaleString('es-ES')}
                  </p>
                </div>
                {selectedAlert.closed_by && (
                  <div>
                    <Label>Cerrado Por</Label>
                    <p className="mt-1 text-gray-900 dark:text-white">{selectedAlert.closed_by.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{selectedAlert.closed_by.email}</p>
                  </div>
                )}
                {selectedAlert.closed_at && (
                  <div>
                    <Label>Fecha de Cierre</Label>
                    <p className="mt-1 text-gray-900 dark:text-white">
                      {new Date(selectedAlert.closed_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                )}
                <div>
                  <Label>Contactos de Emergencia Notificados</Label>
                  <p className="mt-1 text-gray-900 dark:text-white">
                    {selectedAlert.emergency_contacts_notified?.length || 0} contactos
                  </p>
                </div>
                <div>
                  <Label>Estado</Label>
                  <div className="mt-1">
                    <Badge color={selectedAlert.status === 'active' ? 'failure' : 'success'}>
                      {selectedAlert.status === 'active' ? 'Activa' : 'Cerrada'}
                    </Badge>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button color="gray" onClick={() => setShowDetailsModal(false)} className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                Cerrar
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
